/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { FieldGroup, FieldText } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { IInspectorControlStringProps, schemaName } from "./InspectorControlString.common";
import { isModelValueRequired } from "@adapptio/lib-blueprint";

export const InspectorControlString: React.FC<IInspectorControlStringProps> = ({
	model: _model,
	validationErrors: validationErrorsProp,
	label,
	onBlur,
	onFocus,
	readOnly,
	multiline
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, schemaName);
	const inspectorContext = useEditorInspectorContext();

	const required = isModelValueRequired(model);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	return (
		<FieldGroup grow>
			<FieldText
				key={model.nodeId}
				label={label}
				value={model.value}
				onChange={handleChange}
				validationErrors={[ ...(validationErrors || []), ...(validationErrorsProp || []) ]}
				grow
				onBlur={onBlur}
				onFocus={onFocus}
				readOnly={inspectorContext?.readOnly || readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				clearable={!required}
				multiLine={multiline}
			/>
		</FieldGroup>
	);
};

InspectorControlString.defaultProps = {
	label: "",
	validationErrors: []
};
